import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import {
  ActionIcon,
  Box,
  Container,
  Group,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconFolder, IconSearch, IconX } from '@tabler/icons-react';

import ConfirmBar from '@ui-kit/molecules/ConfirmBar';
import dialogStoreVar from '@features/Dialog/store/dialogStore';
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import { vars } from '@app/shared/themes/vars';
import { palette } from '@app/shared/ui-kit/colors';
import {
  useListRequestListFoldersQuery,
  useReclassifyDocumentMutation,
  RecentPendingValidationDocumentsDocument,
  RecentUnclassifiedDocumentsDocument,
  RecentDirectUploadDocumentsDocument,
  RecentClassifiedDocumentsDocument,
  ListRequestListFoldersDocument,
  SortDirection,
  useGetOneResourceQuery,
} from '@app-types/blackroom-api';
import { DialogProps } from '@features/Dialog/types';
import useDatadumpFolderIds from '@features/Resource/hooks/useDatadumpFolderIds';
import { flattenItemsByRank } from '../RequestListTable/utils/transformToNestedData';
import { filterRowsBySearch, normalizeSearchValue } from './utils';

interface FolderOptionButtonProps {
  children: ReactNode;
  indent?: number;
  selected: boolean;
  onClick: () => void;
}

function FolderOptionButton({
  children,
  indent = 0,
  selected,
  onClick,
}: FolderOptionButtonProps) {
  return (
    <UnstyledButton
      onClick={onClick}
      px="sm"
      py={8}
      w="100%"
      style={{
        borderRadius: 6,
        border: selected
          ? `1px solid ${vars.colors.blackroomBlue[5]}`
          : '1px solid transparent',
        backgroundColor: selected ? vars.colors.blackroomBlue[0] : undefined,
      }}
    >
      <Box pl={indent}>{children}</Box>
    </UnstyledButton>
  );
}

interface DialogReclassifyDocumentProps extends DialogProps {
  classificationId?: string;
}

function DialogReclassifyDocument({
  classificationId,
}: DialogReclassifyDocumentProps) {
  const { t } = useTranslation();
  const dialogStore = useReactiveVar(dialogStoreVar);
  const workspace = useWorkspace();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { dataroomFolderId } = useDatadumpFolderIds();

  const { data: itemsData } = useListRequestListFoldersQuery({
    variables: {
      filter: { workspaceId: workspace.id },
      sorter: { rank: SortDirection.Asc },
    },
  });

  const { data: dataroomFolderData } = useGetOneResourceQuery({
    variables: {
      workspaceId: workspace.id,
      resourceId: dataroomFolderId ?? '',
    },
    skip: !dataroomFolderId,
  });

  const rows = useMemo(
    () => flattenItemsByRank(itemsData?.requestListFolders ?? []),
    [itemsData?.requestListFolders],
  );
  const normalizedSearchQuery = useMemo(
    () => normalizeSearchValue(searchQuery.trim()),
    [searchQuery],
  );
  const visibleRows = useMemo(
    () => filterRowsBySearch(rows, normalizedSearchQuery),
    [rows, normalizedSearchQuery],
  );

  const dataroomFolder = dataroomFolderData?.resource;
  const dataroomFolderLabel =
    dataroomFolder?.name ?? t('Resource:Common.resource.dataroom');
  const dataroomFolderMatchesSearch =
    !!dataroomFolderId &&
    (!normalizedSearchQuery ||
      normalizeSearchValue(dataroomFolderLabel).includes(
        normalizedSearchQuery,
      ));
  const showDataroomFolder =
    !!dataroomFolderId &&
    (dataroomFolderMatchesSearch || visibleRows.length > 0);
  const isDataroomFolderSelected =
    !!dataroomFolderId && selectedFolderId === dataroomFolderId;
  const showNoResults =
    !!normalizedSearchQuery && !showDataroomFolder && visibleRows.length === 0;

  const [reclassify, { loading: reclassifying }] =
    useReclassifyDocumentMutation({
      onCompleted: () => {
        showNotification({
          title: t(
            'RequestList:DialogReclassifyDocument.successNotification.title',
          ),
          message: t(
            'RequestList:DialogReclassifyDocument.successNotification.description',
          ),
          color: 'blackroomGreen',
        });
        dialogStore?.closeDialog?.('dialog-reclassify-document');
      },
      onError: () => {
        showNotification({
          title: t('Common:errors.genericTitle'),
          message: t('Common:errors.genericMessage'),
          color: 'blackroomRed',
        });
      },
    });

  const handleConfirm = useCallback(() => {
    if (!classificationId || !selectedFolderId) return;
    reclassify({
      variables: {
        input: {
          classificationId,
          targetFolderId: selectedFolderId,
          workspaceId: workspace.id,
        },
      },
      refetchQueries: [
        RecentPendingValidationDocumentsDocument,
        RecentUnclassifiedDocumentsDocument,
        RecentDirectUploadDocumentsDocument,
        RecentClassifiedDocumentsDocument,
        ListRequestListFoldersDocument,
      ],
    });
  }, [classificationId, selectedFolderId, workspace.id, reclassify]);

  return (
    <Container fluid>
      <Stack gap="md" px="md" pb="md">
        <Text fz="sm" c="dimmed">
          {t('RequestList:DialogReclassifyDocument.description')}
        </Text>

        <TextInput
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.currentTarget.value)}
          placeholder={t(
            'RequestList:DialogReclassifyDocument.searchPlaceholder',
          )}
          leftSection={<IconSearch size={16} />}
          rightSection={
            searchQuery ? (
              <ActionIcon
                aria-label={t(
                  'RequestList:DialogReclassifyDocument.clearSearch',
                )}
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => setSearchQuery('')}
              >
                <IconX size={14} />
              </ActionIcon>
            ) : undefined
          }
        />

        <ScrollArea.Autosize mah={400}>
          <Stack gap={4}>
            {showDataroomFolder && (
              <FolderOptionButton
                onClick={() => setSelectedFolderId(dataroomFolderId ?? null)}
                selected={isDataroomFolderSelected}
              >
                <Group gap={8} wrap="nowrap">
                  <IconFolder size={16} color={palette.blackroomOrange} />
                  <Text fz="sm" fw={600} truncate>
                    {dataroomFolderLabel}
                  </Text>
                </Group>
              </FolderOptionButton>
            )}
            {showNoResults && (
              <Text fz="sm" c="dimmed" ta="center" py="md">
                {t('RequestList:DialogReclassifyDocument.noResults')}
              </Text>
            )}
            {visibleRows.map(({ item, depth }) => {
              const itemIndex = item.index;
              const visualDepth = depth + 1;
              const isSelected = selectedFolderId === item.id;
              return (
                <FolderOptionButton
                  key={item.id}
                  onClick={() => setSelectedFolderId(item.id)}
                  selected={isSelected}
                  indent={visualDepth * 20}
                >
                  <Group gap={8} wrap="nowrap">
                    <IconFolder size={16} color={palette.blackroomBlue} />
                    <Text fz="sm" fw={depth === 0 ? 600 : 400} truncate>
                      {itemIndex ? `${itemIndex} — ` : ''}
                      {item.name || item.requestMetadata?.description || '...'}
                    </Text>
                  </Group>
                </FolderOptionButton>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>
      </Stack>
      <ConfirmBar
        labelProps={{
          confirm: t('Common:cta.save'),
          cancel: t('Common:cta.cancel'),
        }}
        onConfirm={handleConfirm}
        onCancel={() =>
          dialogStore?.closeDialog?.('dialog-reclassify-document')
        }
        disabled={!selectedFolderId}
        saving={reclassifying}
      />
    </Container>
  );
}

export default DialogReclassifyDocument;
